import { isAfter, subSeconds } from 'date-fns';
import { jwtDecode } from 'jwt-decode';

const EXPIRATION_DRIFT = 5; // 5 second drift
const V2_COMPANY_KEY = 'https://bonus.ly/company_id';
const V2_USER_KEY = 'https://bonus.ly/user_id';
const V2_SPOOFING_USER_ID_KEY = 'https://bonus.ly/spoofing_user_id';
const V2_SPOOFING_USER_EMAIL_KEY = 'https://bonus.ly/spoofing_user_email';

interface JWTV2Payload {
	exp: number;
	[V2_COMPANY_KEY]: string;
	[V2_USER_KEY]: string;
	[V2_SPOOFING_USER_ID_KEY]?: string;
	[V2_SPOOFING_USER_EMAIL_KEY]?: string;
}

const isV2JWT = (payload: unknown): payload is JWTV2Payload => {
	return !!payload && typeof payload === 'object' && V2_USER_KEY in payload && V2_COMPANY_KEY in payload;
};

export const parseToken = (token?: string) => {
	const emptyResult = {
		expiresAt: undefined,
		companyId: undefined,
		userId: undefined,
		spoofingUserId: undefined,
		spoofingUserEmail: undefined,
	};

	if (!token) {
		return emptyResult;
	}

	try {
		const decoded = jwtDecode(token);

		if (isV2JWT(decoded)) {
			return {
				expiresAt: decoded.exp ? new Date(decoded.exp * 1000) : undefined,
				companyId: decoded[V2_COMPANY_KEY],
				userId: decoded[V2_USER_KEY],
				spoofingUserId: decoded[V2_SPOOFING_USER_ID_KEY] || undefined,
				spoofingUserEmail: decoded[V2_SPOOFING_USER_EMAIL_KEY] || undefined,
			};
		}

		return emptyResult;
	} catch {
		return emptyResult;
	}
};

export const isExpired = (token: string) => {
	const decoded = parseToken(token);

	if (!decoded.expiresAt) {
		return false;
	}

	// Apply a drift to the expiration time to account for network latency
	const expiration = subSeconds(decoded.expiresAt, EXPIRATION_DRIFT);
	const now = new Date();

	return isAfter(now, expiration);
};
